import React, {PureComponent} from 'react';
import withStyles from '@mui/styles/withStyles';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";

const BADGE_ICONS = {
    // Consistency
    seedling: "🌱", fire: "🔥", muscle: "💪", pillar: "🏛️", star: "⭐", trident: "🔱",
    crown: "👑", diamond: "💎", infinity: "♾️",
    // Streak
    link: "🔗", chain: "⛓️", mountain: "🏔️", rocket: "🚀",
    hourglass: "⏳", calendar: "📅",
    // Strength
    bronze_medal: "🥉", silver_medal: "🥈", gold_medal: "🥇",
    // Volume
    rock: "🏋️", truck: "🚛", anchor: "⚓", crane: "🏗️", factory: "🏭", globe: "🌍", volcano: "🌋",
    // Cardio
    footprints: "👣", shoe: "👟", road: "🛤️", map: "🗺️", earth: "🌏",
    heart: "❤️", heartbeat: "💓", wind: "🌬️",
    // Variety
    compass: "🧭", wheel: "🎡", books: "📚", scroll: "📜",
    // Dedication
    stopwatch: "⏱️", clock: "🕐", runner: "🏃", shield: "🛡️", hammer: "🔨", temple: "⛩️",
    // Milestones
    sunrise: "🌅", moon: "🌙", stars: "🌠", lightning: "⚡", dash: "💨",
    hourglass_full: "⌛", repeat: "🔁", three: "🏅",
    hundred: "💯", one: "☝️", hand: "🖐️", dot: "•", phoenix: "🔄",
    share: "📤", loop: "🔁",
    baby: "👶", pencil: "✏️", mirror: "🪞", brain: "🧠",
    gauge: "📏", clipboard: "📋", explosion: "💥",
    // Journey
    badge_1: "1️⃣", badge_2: "2️⃣", badge_3: "3️⃣", badge_4: "4️⃣", badge_5: "5️⃣",
};

const CATEGORY_META = {
    Consistency: { emoji: "📅", subtitle: "Workouts completed" },
    Streak:      { emoji: "🔥", subtitle: "Consecutive weeks" },
    Strength:    { emoji: "💪", subtitle: "Weight milestones" },
    Volume:      { emoji: "📊", subtitle: "Total weight lifted" },
    Cardio:      { emoji: "🏃", subtitle: "Distance & cardio time" },
    Variety:     { emoji: "🧭", subtitle: "Different exercises" },
    Dedication:  { emoji: "⏱️", subtitle: "Time in the gym" },
    Milestones:  { emoji: "⚡", subtitle: "Special feats" },
    Journey:     { emoji: "🗓️", subtitle: "How long you've been training" },
};

const CATEGORY_ORDER = ["Consistency", "Streak", "Strength", "Volume", "Cardio", "Variety", "Dedication", "Milestones", "Journey"];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = theme => ({
    strip: {
        display: 'flex',
        overflowX: 'auto',
        gap: theme.spacing(1),
        paddingBottom: theme.spacing(0.5),
        paddingTop: theme.spacing(0.5),
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        scrollbarWidth: 'none',
    },
    badge: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 48,
        cursor: 'pointer',
    },
    badgeIcon: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 18,
        border: '2px solid',
    },
    badgeUnlocked: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.08)'
            : 'rgba(25, 118, 210, 0.06)',
        filter: 'saturate(0.95)',
    },
    badgeLocked: {
        borderColor: theme.palette.action.disabled,
        backgroundColor: theme.palette.action.disabledBackground,
        opacity: 0.4,
        filter: 'grayscale(100%)',
    },
    badgeName: {
        fontSize: '0.6rem',
        textAlign: 'center',
        lineHeight: 1.1,
        marginTop: 2,
        maxWidth: 52,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    // Dialog styles
    dialogPaper: {
        borderRadius: '16px 16px 0 0',
        maxHeight: '85vh',
    },
    dialogHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 2, 0, 2),
    },
    dialogCount: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
    },
    categorySection: {
        marginBottom: theme.spacing(2.5),
    },
    categoryHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    categorySubtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
    },
    badgeGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
        gap: theme.spacing(1.5),
    },
    dialogBadge: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(1, 0.5),
        borderRadius: 10,
        transition: 'background 0.15s ease',
    },
    dialogBadgeUnlocked: {
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.06)'
            : 'rgba(25, 118, 210, 0.04)',
    },
    dialogBadgeLocked: {
        opacity: 0.45,
    },
    dialogBadgeIcon: {
        width: 44,
        height: 44,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 22,
        border: '2px solid',
        marginBottom: 4,
    },
    dialogBadgeIconUnlocked: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(144, 202, 249, 0.10)'
            : 'rgba(25, 118, 210, 0.07)',
        filter: 'saturate(0.6)',
    },
    dialogBadgeIconLocked: {
        borderColor: theme.palette.action.disabled,
        backgroundColor: theme.palette.action.disabledBackground,
        filter: 'grayscale(100%)',
    },
    dialogBadgeName: {
        fontSize: '0.72rem',
        fontWeight: 600,
        textAlign: 'center',
        lineHeight: 1.2,
    },
    dialogBadgeDesc: {
        fontSize: '0.62rem',
        textAlign: 'center',
        lineHeight: 1.2,
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    lockIcon: {
        fontSize: 18,
        color: theme.palette.action.disabled,
    },
});

class AchievementBadgesStrip extends PureComponent {
    constructor(props) {
        super(props);
        const cached = UserLocalStorage.get('achievements');
        this.state = {
            badges: cached || null,
            isLoading: !cached,
            isDialogOpen: false,
        };
    }

    componentDidMount() {
        this.fetch();
    }

    fetch = async () => {
        try {
            const response = await fetch(UrlBuilder.user.achievementsApi());
            if (response.status !== 200) {
                this.setState({isLoading: false});
                return;
            }
            const responseData = await response.json();
            const badges = responseData.data || [];
            UserLocalStorage.set('achievements', badges);
            this.setState({
                badges,
                isLoading: false,
            });
        } catch {
            this.setState({isLoading: false});
        }
    }

    getIcon = (iconKey) => {
        return BADGE_ICONS[iconKey] || '❓';
    }

    openDialog = () => {
        this.setState({isDialogOpen: true});
    }

    closeDialog = () => {
        this.setState({isDialogOpen: false});
    }

    groupByCategory = (badges) => {
        const grouped = {};
        CATEGORY_ORDER.forEach(cat => { grouped[cat] = []; });
        badges.forEach(badge => {
            if (grouped[badge.category]) {
                grouped[badge.category].push(badge);
            }
        });
        return grouped;
    }

    renderDialog = () => {
        const {classes} = this.props;
        const {badges, isDialogOpen} = this.state;

        if (!isDialogOpen || !badges) return null;

        const grouped = this.groupByCategory(badges);
        const totalUnlocked = badges.filter(b => b.isUnlocked).length;

        return (
            <Dialog
                open={true}
                onClose={this.closeDialog}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    className: classes.dialogPaper,
                    sx: { position: 'fixed', bottom: 0, m: 0, width: '100%' }
                }}
            >
                <div className={classes.dialogHeader}>
                    <div>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                            Achievements
                        </Typography>
                        <Typography className={classes.dialogCount}>
                            {totalUnlocked} of {badges.length} unlocked
                        </Typography>
                    </div>
                    <IconButton onClick={this.closeDialog} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </div>

                <DialogContent sx={{ px: 2, pb: 3 }}>
                    {CATEGORY_ORDER.map(category => {
                        const categoryBadges = grouped[category];
                        if (!categoryBadges || categoryBadges.length === 0) return null;
                        const meta = CATEGORY_META[category];

                        return (
                            <div key={category} className={classes.categorySection}>
                                <div className={classes.categoryHeader}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                        {meta.emoji} {category}
                                    </Typography>
                                    <Typography className={classes.categorySubtitle}>
                                        {meta.subtitle}
                                    </Typography>
                                </div>

                                <div className={classes.badgeGrid}>
                                    {categoryBadges.map(badge => (
                                        <div
                                            key={badge.key}
                                            className={`${classes.dialogBadge} ${badge.isUnlocked ? classes.dialogBadgeUnlocked : classes.dialogBadgeLocked}`}
                                        >
                                            <div className={`${classes.dialogBadgeIcon} ${badge.isUnlocked ? classes.dialogBadgeIconUnlocked : classes.dialogBadgeIconLocked}`}>
                                                {badge.isUnlocked
                                                    ? this.getIcon(badge.icon)
                                                    : <LockOutlinedIcon className={classes.lockIcon} />
                                                }
                                            </div>
                                            <Typography
                                                className={classes.dialogBadgeName}
                                                color={badge.isUnlocked ? "textPrimary" : "textSecondary"}
                                            >
                                                {badge.name}
                                            </Typography>
                                            <Typography className={classes.dialogBadgeDesc}>
                                                {badge.description}
                                            </Typography>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </DialogContent>
            </Dialog>
        );
    }

    render() {
        const {classes} = this.props;
        const {badges, isLoading} = this.state;

        if (isLoading) {
            return <Box pt={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Skeleton variant="text" width={80} height={18}/>
                    <Skeleton variant="rounded" width={90} height={20} sx={{borderRadius: 2}}/>
                </Box>
                <Box className={classes.strip}>
                    {Array.from({length: 6}, (_, i) =>
                        <div key={i} className={classes.badge}>
                            <Skeleton variant="circular" width={36} height={36}/>
                            <Skeleton variant="text" width={36} height={12} sx={{mt: '2px'}}/>
                        </div>
                    )}
                </Box>
            </Box>;
        }

        if (!badges || badges.length === 0) {
            return null;
        }

        const totalUnlocked = badges.filter(b => b.isUnlocked).length;
        const totalBadges = badges.length;

        // Strip shows per progressive category: highest unlocked + next locked one coming
        const PROGRESSIVE_CATEGORIES = ["Consistency", "Streak", "Strength", "Volume", "Cardio", "Variety", "Dedication"];
        const displayBadges = [];
        PROGRESSIVE_CATEGORIES.forEach(cat => {
            const catBadges = badges.filter(b => b.category === cat);
            const catUnlocked = catBadges.filter(b => b.isUnlocked);
            const catLocked = catBadges.filter(b => !b.isUnlocked);
            if (catUnlocked.length > 0) {
                // Show highest unlocked
                displayBadges.push(catUnlocked[catUnlocked.length - 1]);
                // Show next locked if there is one
                if (catLocked.length > 0) {
                    displayBadges.push(catLocked[0]);
                }
            } else if (catBadges.length > 0) {
                // Nothing unlocked — show first locked as teaser
                displayBadges.push(catBadges[0]);
            }
        });

        return (
            <Box pt={1}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={0.5}
                    onClick={this.openDialog}
                    sx={{ cursor: 'pointer' }}
                >
                    <Typography variant="caption" color="textSecondary">
                        Achievements
                    </Typography>
                    <Typography
                        variant="caption"
                        color="textPrimary"
                        sx={{
                            fontWeight: 500,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1,
                            py: 0.25,
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}
                    >
                        {totalUnlocked}/{totalBadges} &middot; View all
                    </Typography>
                </Box>
                <Box className={classes.strip} onClick={this.openDialog} sx={{ cursor: 'pointer' }}>
                    {displayBadges.map(badge =>
                        <Tooltip
                            key={badge.key}
                            title={badge.isUnlocked
                                ? `${badge.name}: ${badge.description}`
                                : `${badge.name}: ${badge.description} (locked)`
                            }
                            arrow
                            enterTouchDelay={0}
                        >
                            <div className={classes.badge}>
                                <div className={`${classes.badgeIcon} ${badge.isUnlocked ? classes.badgeUnlocked : classes.badgeLocked}`}>
                                    {this.getIcon(badge.icon)}
                                </div>
                                <Typography
                                    className={classes.badgeName}
                                    color={badge.isUnlocked ? "textPrimary" : "textSecondary"}
                                >
                                    {badge.name}
                                </Typography>
                            </div>
                        </Tooltip>
                    )}
                </Box>

                {this.renderDialog()}
            </Box>
        );
    }
}

export default withStyles(useStyles)(AchievementBadgesStrip);
